/** Local transcription history, stored ONLY in the user's browser (IndexedDB).
 *
 * This is the privacy model of the product: the server erases transcription
 * texts within hours, so the user's lasting copy lives here on their device.
 * Every function is no-throw — a browser without IndexedDB (or in a locked
 * private mode) simply behaves as if the history were empty.
 */
import type { OutputFormat } from '../types/transcription'

export interface HistoryEntry {
  /** Server job id — stable key, also ties polling updates to the entry */
  id: string
  filename: string
  /** STT language code the audio was transcribed with (e.g. ru_RU) */
  language: string
  /** Format chosen for the charged request */
  format: OutputFormat
  fileSize: number
  createdAt: number
  /** Every server-delivered format collected so far: {format: content} */
  formats: Record<string, string>
  /** True once the background sweep finished (missing ones derive client-side) */
  complete: boolean
  /** SHA-256 of the uploaded file — used to catch duplicate uploads */
  hash?: string
  /** Format the user last VIEWED (may differ from the charged one) — the
   * history card reopens on it instead of resetting to the charged format */
  viewFormat?: string
  /** Cues after subtitle segmentation: computed once, during transcription,
   * while the audio is still in hand. /history can't recompute them — the file
   * is never stored — so the good cut is saved here instead. Absent on entries
   * made before the feature (and dropped once the user edits a line). */
  segmented?: { start: number; end: number; text: string }[]
}

const DB_NAME = 'stt-history'
const STORE = 'transcriptions'

function createStore(db: IDBDatabase): void {
  if (!db.objectStoreNames.contains(STORE)) {
    db.createObjectStore(STORE, { keyPath: 'id' })
  }
}

/** Open the history database, creating (or repairing) its store.
 *
 * Opened WITHOUT a fixed version on purpose. A database can end up at our name
 * with no store inside — an upgrade interrupted halfway, or another tool that
 * opened the name first — and asking for a fixed version then never runs the
 * upgrade path again: every read and write fails silently and the user's
 * history is gone for good with no error anywhere. Versionless opening takes
 * whatever exists, and a single version bump rebuilds a missing store.
 */
function openDb(retry = true): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    if (typeof indexedDB === 'undefined') return resolve(null)
    try {
      const req = indexedDB.open(DB_NAME)
      req.onupgradeneeded = () => createStore(req.result)
      req.onsuccess = () => {
        const db = req.result
        if (db.objectStoreNames.contains(STORE)) return resolve(db)
        if (!retry) {
          db.close()
          return resolve(null)
        }
        const next = db.version + 1
        db.close()
        try {
          const repair = indexedDB.open(DB_NAME, next)
          repair.onupgradeneeded = () => createStore(repair.result)
          repair.onsuccess = () => {
            const healed = repair.result
            if (healed.objectStoreNames.contains(STORE)) return resolve(healed)
            healed.close()
            resolve(null)
          }
          repair.onerror = () => resolve(null)
          repair.onblocked = () => resolve(null)
        } catch {
          resolve(null)
        }
      }
      req.onerror = () => resolve(null)
      req.onblocked = () => resolve(null)
    } catch {
      resolve(null)
    }
  })
}

async function withStore<T>(
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T | null> {
  const db = await openDb()
  if (!db) return null
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, mode)
      const req = run(tx.objectStore(STORE))
      req.onsuccess = () => resolve(req.result)
      req.onerror = () => resolve(null)
      tx.oncomplete = () => db.close()
      tx.onabort = () => {
        db.close()
        resolve(null)
      }
    } catch {
      db.close()
      resolve(null)
    }
  })
}

/** Read-modify-write inside ONE transaction. The formats poll, the view
 * format and the segmentation pass all update the same record, sometimes in
 * the same instant: with a separate read and write, the last writer would put
 * back a record it read before the others' changes and silently drop them. */
async function mutateEntry(
  id: string,
  change: (entry: HistoryEntry) => HistoryEntry | null,
): Promise<void> {
  const db = await openDb()
  if (!db) return
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, 'readwrite')
      const store = tx.objectStore(STORE)
      const req = store.get(id)
      req.onsuccess = () => {
        const existing = req.result as HistoryEntry | undefined
        if (!existing) return
        const next = change(existing)
        if (next) store.put(next)
      }
      const done = () => {
        db.close()
        resolve()
      }
      tx.oncomplete = done
      tx.onabort = done
      tx.onerror = done
    } catch {
      db.close()
      resolve()
    }
  })
}

export async function saveHistoryEntry(entry: HistoryEntry): Promise<void> {
  // One entry per unique audio+language: re-transcribing the same file
  // replaces the old entry instead of piling up duplicates
  if (entry.hash) {
    const all = (await withStore('readonly', (s) => s.getAll())) as HistoryEntry[] | null
    for (const e of all ?? []) {
      if (e.id !== entry.id && e.hash === entry.hash && e.language === entry.language) {
        await withStore('readwrite', (s) => s.delete(e.id))
      }
    }
  }
  await withStore('readwrite', (s) => s.put(entry))
}

/** Collapse duplicates left from before deduplication: for each audio+language
 * keep only the newest entry. Returns how many entries were removed. */
export async function pruneDuplicateHistory(): Promise<number> {
  const all = (await withStore('readonly', (s) => s.getAll())) as HistoryEntry[] | null
  if (!all) return 0
  const newest = new Map<string, HistoryEntry>()
  for (const e of all) {
    if (!e.hash) continue
    const key = `${e.hash}:${e.language}`
    const seen = newest.get(key)
    if (!seen || e.createdAt > seen.createdAt) newest.set(key, e)
  }
  let pruned = 0
  for (const e of all) {
    if (!e.hash) continue
    if (newest.get(`${e.hash}:${e.language}`)?.id !== e.id) {
      await withStore('readwrite', (s) => s.delete(e.id))
      pruned++
    }
  }
  return pruned
}

/** Merge newly collected server formats into an existing entry. */
export async function updateHistoryFormats(
  id: string,
  formats: Record<string, string>,
  complete: boolean,
): Promise<void> {
  await mutateEntry(id, (existing) => ({
    ...existing,
    formats: { ...existing.formats, ...formats },
    complete,
  }))
}

/** Store (or drop) the subtitle-segmented cues of an entry. An optional field
 * on a store created without indexes — no DB_VERSION bump, old entries simply
 * don't have it. */
export async function updateHistorySegmented(
  id: string,
  segmented: { start: number; end: number; text: string }[] | null,
): Promise<void> {
  await mutateEntry(id, (existing) => {
    const next = { ...existing }
    if (segmented) next.segmented = segmented
    else delete next.segmented
    return next
  })
}

/** Remember which format the user was looking at, so reopening the entry
 * (or reloading the page) doesn't reset the view to the charged format. */
export async function updateHistoryViewFormat(id: string, viewFormat: string): Promise<void> {
  await mutateEntry(id, (existing) =>
    existing.viewFormat === viewFormat ? null : { ...existing, viewFormat },
  )
}

export async function listHistory(): Promise<HistoryEntry[]> {
  const all = (await withStore('readonly', (s) => s.getAll())) as HistoryEntry[] | null
  if (!all) return []
  return all.sort((a, b) => b.createdAt - a.createdAt)
}

/** Earlier transcription of the same audio content in the same language. */
export async function findHistoryByHash(
  hash: string,
  language: string,
): Promise<HistoryEntry | null> {
  const all = await listHistory()
  return all.find((e) => e.hash === hash && e.language === language) ?? null
}

export async function deleteHistoryEntry(id: string): Promise<void> {
  await withStore('readwrite', (s) => s.delete(id))
}

export async function clearHistory(): Promise<void> {
  await withStore('readwrite', (s) => s.clear())
}
